import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  Search as SearchSdkType,
  NonNullablePaths,
  SearchSpec,
} from '@wix/sdk-types';
import * as ambassadorWixSearchPlatformizedV1SiteDocument from './search-platformized-v1-site-document-wix-site-search.http.js';
import { createSearchUtils } from '@wix/sdk-runtime/search-builder-utils';

export interface SiteDocument {
  /**
   * Result ID.
   * @readonly
   * @maxLength 120
   */
  _id?: string;
  /** The document payload. */
  data?: Record<string, any> | null;
}

export interface SearchRequest {
  /** Search query and aggregation information. */
  search: Search;
  /** Document type to search in. */
  documentType: DocumentTypeWithLiterals;
  /**
   * Language to search in.
   * @format LANGUAGE
   */
  language?: string | null;
}

export interface Search extends SearchPagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * A search method for grouping data into various categories (facets) and providing summaries for each category. For example, use aggregations to categorize search results by specific price ranges, brand names, or ratings.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Search information. */
  search?: SearchDetails;
}

/** @oneof */
export interface SearchPagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @minLength 2
   * @maxLength 100
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Aggregation extends AggregationKindOneOf {
  /** Pass if `type` is `VALUE`. A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number of products (count) for each price listed in the store. */
  value?: ValueAggregation;
  /** Pass if `type` is `SCALAR`. A scalar aggregation calculates a single numerical value from a dataset, such as the total, minimum, or maximum value, summarizing the dataset into one key metric. For example, use a scalar aggregation to get the minimum price listed in a store. */
  scalar?: ScalarAggregation;
  /** Pass if `type` is `NESTED`. A nested aggregation is applied to the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. This allows for more complex analyses that summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on the field containing the price, and a second value aggregation on the field indicating whether a product is in stock. You can nest up to a maximum of 3 aggregations. Each aggregation can be either value-based or scalar, allowing flexibility in how the data is grouped and analyzed. */
  nested?: NestedAggregation;
  /**
   * Aggregation name displayed in the return.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation to perform. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** Pass if `type` is `VALUE`. A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number of products (count) for each price listed in the store. */
  value?: ValueAggregation;
  /** Pass if `type` is `SCALAR`. A scalar aggregation calculates a single numerical value from a dataset, such as the total, minimum, or maximum value, summarizing the dataset into one key metric. For example, use a scalar aggregation to get the minimum price listed in a store. */
  scalar?: ScalarAggregation;
  /** Pass if `type` is `NESTED`. A nested aggregation is applied to the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. This allows for more complex analyses that summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on the field containing the price, and a second value aggregation on the field indicating whether a product is in stock. You can nest up to a maximum of 3 aggregations. Each aggregation can be either value-based or scalar, allowing flexibility in how the data is grouped and analyzed. */
  nested?: NestedAggregation;
}

export enum ScalarType {
  /** Unknown scalar type. */
  UNKNOWN_SCALAR_TYPE = 'UNKNOWN_SCALAR_TYPE',
  /** Minimum value. */
  MIN = 'MIN',
  /** Maximum value. */
  MAX = 'MAX',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'UNKNOWN_SCALAR_TYPE'
  | 'MIN'
  | 'MAX';

export enum NestedAggregationType {
  /** Unknown aggregation type. */
  UNKNOWN_AGGREGATION_TYPE = 'UNKNOWN_AGGREGATION_TYPE',
  /** An aggregation where result buckets are dynamically built - one per unique value. */
  VALUE = 'VALUE',
  /** A single-value metric aggregation - e.g. min, max, sum, avg. */
  SCALAR = 'SCALAR',
}

/** @enumType */
export type NestedAggregationTypeWithLiterals =
  | NestedAggregationType
  | 'UNKNOWN_AGGREGATION_TYPE'
  | 'VALUE'
  | 'SCALAR';

export interface ValueAggregation {
  /**
   * Maximum number of aggregation results to return.
   * Min: `1`
   * Max: `250`
   * Default: `10`
   */
  limit?: number | null;
}

export interface ScalarAggregation {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
}

export interface NestedAggregationItem extends NestedAggregationItemKindOneOf {
  /** Pass if `type` is `VALUE`. A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number of products (count) for each price listed in the store. */
  value?: ValueAggregation;
  /** Pass if `type` is `SCALAR`. A scalar aggregation calculates a single numerical value from a dataset, such as the total, minimum, or maximum value, summarizing the dataset into one key metric. For example, use a scalar aggregation to get the minimum price listed in a store. */
  scalar?: ScalarAggregation;
  /**
   * Aggregation name displayed in the return.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation to perform. */
  type?: NestedAggregationTypeWithLiterals;
  /**
   * Field to aggregate by.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationItemKindOneOf {
  /** Pass if `type` is `VALUE`. A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number of products (count) for each price listed in the store. */
  value?: ValueAggregation;
  /** Pass if `type` is `SCALAR`. A scalar aggregation calculates a single numerical value from a dataset, such as the total, minimum, or maximum value, summarizing the dataset into one key metric. For example, use a scalar aggregation to get the minimum price listed in a store. */
  scalar?: ScalarAggregation;
}

export enum AggregationType {
  UNKNOWN_AGGREGATION_TYPE = 'UNKNOWN_AGGREGATION_TYPE',
  /** An aggregation where result buckets are dynamically built - one per unique value. */
  VALUE = 'VALUE',
  /** A single-value metric aggregation - e.g. min, max, sum, avg. */
  SCALAR = 'SCALAR',
  /** Multi-level aggregation, where each next aggregation is nested within previous one. */
  NESTED = 'NESTED',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'UNKNOWN_AGGREGATION_TYPE'
  | 'VALUE'
  | 'SCALAR'
  | 'NESTED';

/** List of aggregations. Each aggregation is nested within the previous one. */
export interface NestedAggregation {
  /**
   * List of aggregations, where each aggregation is nested within previous one.
   * @minSize 2
   * @maxSize 3
   */
  nestedAggregations?: NestedAggregationItem[];
}

export interface SearchDetails {
  /**
   * Search term or expression.
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in.
   * If the array is empty, all fields are searched.
   * @maxSize 10
   * @maxLength 50
   */
  fields?: string[];
  /** Whether to allow the search function to automatically correct typos or minor mistakes in the search expression. The search function uses an algorithm to find results that are close to the text provided. */
  fuzzy?: boolean;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export enum DocumentType {
  UNSPECIFIED = 'UNSPECIFIED',
  BLOG_POSTS = 'BLOG_POSTS',
  BOOKING_SERVICES = 'BOOKING_SERVICES',
  EVENTS = 'EVENTS',
  FORUM_CONTENT = 'FORUM_CONTENT',
  ONLINE_PROGRAMS = 'ONLINE_PROGRAMS',
  PROGALLERY_ITEM = 'PROGALLERY_ITEM',
  STORES_PRODUCTS = 'STORES_PRODUCTS',
}

/** @enumType */
export type DocumentTypeWithLiterals =
  | DocumentType
  | 'UNSPECIFIED'
  | 'BLOG_POSTS'
  | 'BOOKING_SERVICES'
  | 'EVENTS'
  | 'FORUM_CONTENT'
  | 'ONLINE_PROGRAMS'
  | 'PROGALLERY_ITEM'
  | 'STORES_PRODUCTS';

export interface SearchResponse extends SearchResponsePagingOneOf {
  /** Paging metadata. */
  pagingOffsetMetadata?: PagingMetadata;
  /** Documents matching the search query. */
  siteDocumentItems?: SiteDocument[];
  /** Aggregated data. */
  aggregationData?: AggregationData;
}

/** @oneof */
export interface SearchResponsePagingOneOf {
  /** Paging metadata. */
  pagingOffsetMetadata?: PagingMetadata;
}

export interface AggregationData {
  /**
   * List of the aggregated data results.
   * @maxSize 10000
   */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /**
   * Value contained in the field specified in `fieldPath` for this aggregation in the request.
   * @maxLength 100
   */
  value?: string;
  /** Number of documents containing the specified value in the specified field. */
  count?: number;
}

export interface ValueResults {
  /**
   * List of value aggregation results.
   * @maxSize 250
   */
  results?: ValueAggregationResult[];
}

export interface AggregationResultsScalarResult {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation. For example, the minimum, maximum, or total value for the specified field. */
  value?: number;
}

export interface ValueResult {
  /**
   * Value contained in the field specified in `fieldPath` for this aggregation in the request.
   * @maxLength 1000
   */
  value?: string;
  /** Number of documents containing the specified value in the specified field. */
  count?: number | null;
}

export interface ScalarResult {
  /** Scalar aggregation results. */
  value?: number;
}

export interface NestedResultValue extends NestedResultValueResultOneOf {
  /** Value aggregation results. */
  value?: ValueResult;
  /** Scalar aggregation results. */
  scalar?: ScalarResult;
}

/** @oneof */
export interface NestedResultValueResultOneOf {
  /** Value aggregation results. */
  value?: ValueResult;
  /** Scalar aggregation results. */
  scalar?: ScalarResult;
}

export interface Results {
  /** Aggregation results. */
  results?: Record<string, NestedResultValue>;
}

/**
 * Results of `NESTED` aggregation type in a flattened form
 * aggregations in resulting array are keyed by requested aggregation `name`.
 */
export interface NestedResults {
  /**
   * List of nested aggregation results.
   * @maxSize 1000
   */
  results?: Results[];
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Nested aggregation results. */
  nested?: NestedResults;
  /**
   * Aggregation name defined in the request.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that was performed. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field the data was aggregated by.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Nested aggregation results. */
  nested?: NestedResults;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Whether the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface AutocompleteRequest {
  /**
   * Query phrase to fetch completions for.
   * @maxLength 50
   */
  query: string | null;
  /** Document type to use to search for phrases. */
  documentType: DocumentTypeWithLiterals;
  /**
   * Limit of phrases to fetch.
   * @min 1
   * @max 100
   */
  limit?: number;
  /**
   * Language to search in.
   * @format LANGUAGE
   */
  language?: string | null;
  /** Filter in platformized query language (for example {'field': {'$eq': 'value'}}) */
  filter?: Record<string, any> | null;
  /**
   * Autocompletable fields to use for query completion. If not provided, all autocompletable fields in schema are used
   * @maxSize 10
   * @maxLength 50
   */
  searchFields?: string[];
}

export interface AutocompleteResponse {
  /** Suggested phrases. */
  values?: AutocompleteResponseValue[];
}

export interface AutocompleteResponseValue {
  /**
   * Suggested phrase.
   * @maxLength 100
   */
  query?: string;
}

export interface SuggestRequest {
  /** Document type of documents to search for. */
  documentType: DocumentTypeWithLiterals;
  /** Suggest query information. */
  suggest: Suggest;
  /**
   * Number of suggested document to return.
   * @min 1
   * @max 100
   */
  limit?: number;
  /**
   * Language to search in.
   * @format LANGUAGE
   */
  language?: string | null;
}

export interface Suggest extends SuggestPagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 10
   */
  sort?: Sorting[];
  /** Search information. */
  suggest?: SuggestDetails;
}

/** @oneof */
export interface SuggestPagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
}

export interface SuggestDetails {
  /**
   * Search term or expression.
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in.
   * If the array is empty, all fields are searched.
   * @maxSize 10
   * @maxLength 50
   */
  fields?: string[];
}

export interface SuggestResponse {
  /** Suggested documents. */
  siteDocumentItems?: SiteDocument[];
}

/**
 * Retrieves a list of site documents that match the provided search query and optionally performs aggregations on the data queried.
 *
 * The `search()` API supports the document types listed in the [Introduction](https://dev.wix.com/docs/sdk/backend-modules/search/wix-site-search/introduction), each with its own schema. These schemas define the fields available for filtering, sorting, and free-text searching.
 *
 * To learn more about working with the search query, see [API Query Language](https://dev.wix.com/docs/sdk/articles/working-with-the-sdk/api-query-language).
 * @param search - Search query and aggregation information.
 * @public
 * @documentationMaturity preview
 * @requiredField options.documentType
 * @requiredField search
 * @permissionId SEARCH.SITE_DOCUMENT_READ
 * @applicableIdentity APP
 * @fqn wix.search.platformized.v1.SiteSearchPlatformizedService.Search
 */
export async function search(
  search: SiteDocumentSearch,
  options?: NonNullablePaths<SearchOptions, `documentType`, 2>
): Promise<
  NonNullablePaths<
    SearchResponse,
    | `siteDocumentItems`
    | `siteDocumentItems.${number}._id`
    | `aggregationData.results`
    | `aggregationData.results.${number}.scalar.type`
    | `aggregationData.results.${number}.scalar.value`
    | `aggregationData.results.${number}.name`
    | `aggregationData.results.${number}.type`
    | `aggregationData.results.${number}.fieldPath`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    search: search,
    documentType: options?.documentType,
    language: options?.language,
  });

  const reqOpts = ambassadorWixSearchPlatformizedV1SiteDocument.search(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          search: '$[0]',
          documentType: '$[1].documentType',
          language: '$[1].language',
        },
        singleArgumentUnchanged: false,
      },
      ['search', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SearchOptions {
  /** Document type to search in. */
  documentType: DocumentTypeWithLiterals;
  /**
   * Language to search in.
   * @format LANGUAGE
   */
  language?: string | null;
}

export interface SiteDocumentSearchSpec extends SearchSpec {
  paging: 'offset';
  wql: [];
}

export type CommonSearchWithEntityContext = SearchSdkType<
  SiteDocument,
  SiteDocumentSearchSpec
>;
export type SiteDocumentSearch = {
  /** 
  Paging options to limit and skip the number of items.  
  */
  paging?: {
    /** 
  Number of items to load.  
  */
    limit?:
      | NonNullable<CommonSearchWithEntityContext['paging']>['limit']
      | null;
    /** 
  Number of items to skip in the current sort order.  
  */
    offset?:
      | NonNullable<CommonSearchWithEntityContext['paging']>['offset']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonSearchWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 10 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @minLength: 2,
  @maxLength: 100 
  */
    fieldName?: NonNullable<
      CommonSearchWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonSearchWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  A search method for grouping data into various categories (facets) and providing summaries for each category. For example, use aggregations to categorize search results by specific price ranges, brand names, or ratings. 
  @maxSize: 10 
  */
  aggregations?: {
    /** 
  Pass if `type` is `VALUE`. A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number of products (count) for each price listed in the store.  
  */
    value?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['value'];
    /** 
  Pass if `type` is `SCALAR`. A scalar aggregation calculates a single numerical value from a dataset, such as the total, minimum, or maximum value, summarizing the dataset into one key metric. For example, use a scalar aggregation to get the minimum price listed in a store.  
  */
    scalar?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['scalar'];
    /** 
  Pass if `type` is `NESTED`. A nested aggregation is applied to the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. This allows for more complex analyses that summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on the field containing the price, and a second value aggregation on the field indicating whether a product is in stock. You can nest up to a maximum of 3 aggregations. Each aggregation can be either value-based or scalar, allowing flexibility in how the data is grouped and analyzed.  
  */
    nested?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['nested'];
    /** 
  Aggregation name displayed in the return. 
  @maxLength: 100 
  */
    name?:
      | NonNullable<
          CommonSearchWithEntityContext['aggregations']
        >[number]['name']
      | null;
    /** 
  Type of aggregation to perform.  
  */
    type?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['type'];
    /** 
  Field to aggregate by. 
  @maxLength: 200 
  */
    fieldPath?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['fieldPath'];
  }[];
  /** 
  Search information.  
  */
  search?: {
    /** 
  Search term or expression. 
  @maxLength: 100 
  */
    expression?:
      | NonNullable<CommonSearchWithEntityContext['search']>['expression']
      | null;
    /** 
  Fields to search in.
  If the array is empty, all fields are searched. 
  @maxSize: 10,
  @maxLength: 50 
  */
    fields?: NonNullable<CommonSearchWithEntityContext['search']>['fields'];
    /** 
  Whether to allow the search function to automatically correct typos or minor mistakes in the search expression. The search function uses an algorithm to find results that are close to the text provided.  
  */
    fuzzy?: NonNullable<CommonSearchWithEntityContext['search']>['fuzzy'];
  };
};

export const utils = {
  search: {
    ...createSearchUtils<
      SiteDocument,
      SiteDocumentSearchSpec,
      SiteDocumentSearch
    >(),
  },
};

/**
 * Provides phrase completion. For example "blu" could return "blue", "blues" and "blunt" as candidate phrases.
 * This operation is resource heavy at index time and is reserved for special use cases.
 * @param query - Query phrase to fetch completions for.
 * @param documentType - Document type to use to search for phrases.
 * @internal
 * @documentationMaturity preview
 * @requiredField documentType
 * @requiredField query
 * @permissionId SEARCH.SITE_DOCUMENT_READ
 * @applicableIdentity APP
 * @fqn wix.search.platformized.v1.SiteSearchPlatformizedService.Autocomplete
 */
export async function autocomplete(
  query: string,
  documentType: DocumentTypeWithLiterals,
  options?: AutocompleteOptions
): Promise<
  NonNullablePaths<AutocompleteResponse, `values` | `values.${number}.query`, 4>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    documentType: documentType,
    limit: options?.limit,
    language: options?.language,
    filter: options?.filter,
    searchFields: options?.searchFields,
  });

  const reqOpts =
    ambassadorWixSearchPlatformizedV1SiteDocument.autocomplete(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          query: '$[0]',
          documentType: '$[1]',
          limit: '$[2].limit',
          language: '$[2].language',
          filter: '$[2].filter',
          searchFields: '$[2].searchFields',
        },
        singleArgumentUnchanged: false,
      },
      ['query', 'documentType', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AutocompleteOptions {
  /**
   * Limit of phrases to fetch.
   * @min 1
   * @max 100
   */
  limit?: number;
  /**
   * Language to search in.
   * @format LANGUAGE
   */
  language?: string | null;
  /** Filter in platformized query language (for example {'field': {'$eq': 'value'}}) */
  filter?: Record<string, any> | null;
  /**
   * Autocompletable fields to use for query completion. If not provided, all autocompletable fields in schema are used
   * @maxSize 10
   * @maxLength 50
   */
  searchFields?: string[];
}

/**
 * Executes search query to fetch suggested items. Unlike search query, suggest will match
 * partial phrases (for example "blu" will match documents containing "blue", "blues" and "blunt").
 * Phrase needs to be at least 3 symbols long. Suggestions can also perform optimizations in search
 * results and generally do not guarantee the same level of quality as regular Search endpoint.
 * @param documentType - Document type of documents to search for.
 * @internal
 * @documentationMaturity preview
 * @requiredField documentType
 * @requiredField options.suggest
 * @permissionId SEARCH.SITE_DOCUMENT_READ
 * @applicableIdentity APP
 * @fqn wix.search.platformized.v1.SiteSearchPlatformizedService.Suggest
 */
export async function suggest(
  documentType: DocumentTypeWithLiterals,
  options?: NonNullablePaths<SuggestOptions, `suggest`, 2>
): Promise<
  NonNullablePaths<
    SuggestResponse,
    `siteDocumentItems` | `siteDocumentItems.${number}._id`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    documentType: documentType,
    suggest: options?.suggest,
    limit: options?.limit,
    language: options?.language,
  });

  const reqOpts =
    ambassadorWixSearchPlatformizedV1SiteDocument.suggest(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          documentType: '$[0]',
          suggest: '$[1].suggest',
          limit: '$[1].limit',
          language: '$[1].language',
        },
        singleArgumentUnchanged: false,
      },
      ['documentType', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SuggestOptions {
  /** Suggest query information. */
  suggest: Suggest;
  /**
   * Number of suggested document to return.
   * @min 1
   * @max 100
   */
  limit?: number;
  /**
   * Language to search in.
   * @format LANGUAGE
   */
  language?: string | null;
}
